import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BRAND, type InquiryOrder, type InquiryStatus, type PageView } from "./types";
import { mapAiOrderToInquiry } from "./types";
import { fetchAiOrderList } from "@/api/aiOrder";
import InquiryCard from "./components/InquiryCard";
import InquiryDetailPage from "./components/InquiryDetailPage";
import QuoteConfirmPage from "./components/QuoteConfirmPage";
import PaymentPage from "./components/PaymentPage";

/** 筛选 key → 后端 order_status 值 */
const FILTER_STATUS_MAP: Record<string, number | undefined> = {
  all: undefined,
  "待处理": 0,
  "进行中": undefined, // 前端聚合：处理中 + 待填写
  "待支付": 5,
  "已完成": undefined, // 前端聚合：待使用 + 已完成
};

export default function InquiryOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<InquiryOrder[]>([]);
  const [filter, setFilter] = useState<"all" | InquiryStatus>("all");
  const [selectedOrder, setSelectedOrder] = useState<InquiryOrder | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | string | null>(null);
  const [pageView, setPageView] = useState<PageView>("list");
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  /** 原始列表数据（保留 task_id 用于详情请求） */
  const [rawList, setRawList] = useState<Array<{ id: number; task_type: string }>>([]);

  /** 请求数据 */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const orderStatus = FILTER_STATUS_MAP[filter];
      const res = await fetchAiOrderList({
        page: 1,
        limit: 50,
        ...(orderStatus != null ? { order_status: orderStatus } : {}),
      });
      const list = res.data?.data?.list ?? [];
      setRawList(list.map((item) => ({ id: item.id, task_type: item.task_type })));
      setOrders(list.map(mapAiOrderToInquiry));
      setTotalCount(res.data?.data?.count ?? list.length);
    } catch (err) {
      console.error("[inquiry-orders] 加载失败:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // 首次加载
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusFilters: Array<{ key: "all" | InquiryStatus | "进行中"; label: string }> = [
    { key: "all", label: "全部" },
    { key: "待处理", label: "待处理" },
    { key: "进行中", label: "进行中" },
    { key: "待支付", label: "待支付" },
    { key: "已完成", label: "已完成" },
  ];

  const filtered = filter === "all" ? orders : orders.filter((o) => {
    if (filter === "进行中") return ["处理中", "待填写"].includes(o.status);
    if (filter === "已完成") return ["待使用", "已完成"].includes(o.status);
    return o.status === filter;
  });

  const handleFilterChange = (key: "all" | InquiryStatus | "进行中") => {
    setFilter(key);
  };

  const handleCardClick = (order: InquiryOrder, rawTaskId?: number | string) => {
    setSelectedOrder(order);
    setSelectedTaskId(rawTaskId ?? order.id);
    setPageView("detail");
  };

  const handleViewQuote = () => setPageView("quote");
  const handlePay = () => setPageView("payment");
  const handlePaySuccess = () => {
    if (selectedOrder) {
      setOrders((prev) =>
        prev.map((o) =>
          o.id === selectedOrder.id
            ? { ...o, status: "已成单" as InquiryStatus, convertedOrderId: `ORD${Date.now()}` }
            : o
        )
      );
    }
    setPageView("detail");
  };

  // 详情页
  if (pageView === "detail" && selectedTaskId) {
    return (
      <InquiryDetailPage
        taskId={selectedTaskId}
        onBack={() => setPageView("list")}
        onViewQuote={handleViewQuote}
        onViewOrder={() => navigate("/order/detail/multi")}
      />
    );
  }

  // 方案确认页
  if (pageView === "quote" && selectedOrder) {
    return (
      <QuoteConfirmPage
        order={selectedOrder}
        onBack={() => setPageView("detail")}
        onConfirm={handlePay}
      />
    );
  }

  // 支付页
  if (pageView === "payment" && selectedOrder) {
    return (
      <PaymentPage
        order={selectedOrder}
        onBack={() => setPageView("quote")}
        onSuccess={handlePaySuccess}
      />
    );
  }

  // 列表页
  return (
    <div
      className="min-h-screen pb-10"
      style={{ background: "#F4F5F9", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-40 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg,#1A2238 0%,#2D3561 70%,#3D5A80 100%)" }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{ width: 200, height: 200, top: -50, right: -30, background: "rgba(255,107,53,0.1)", filter: "blur(40px)" }}
          />
        </div>
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
            >
              <i className="ri-arrow-left-line" style={{ fontSize: 18, color: "#fff" }} />
            </button>
            <div className="flex-1">
              <h1 className="text-base font-bold text-white">需求询单</h1>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>
                AI智能规划 · 顾问专属跟进 · 线下确认成单
              </p>
            </div>
            <div
              className="w-9 h-9 flex items-center justify-center rounded-full"
              style={{ background: "rgba(254 122 58 / 1)" }}
            >
              <span className="text-white font-bold text-sm">{totalCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 状态筛选 */}
      <div
        className="sticky z-30 bg-white px-4 py-2.5"
        style={{ top: 104, borderBottom: "1px solid #F0F0F0" }}
      >
        <div className="flex gap-2 overflow-x-auto">
          {statusFilters.map((f) => {
            const cnt = f.key === "all"
              ? orders.length
              : f.key === "进行中"
                ? orders.filter((o) => ["处理中", "待填写"].includes(o.status)).length
                : f.key === "已完成"
                  ? orders.filter((o) => ["待使用", "已完成"].includes(o.status)).length
                  : orders.filter((o) => o.status === f.key).length;
            return (
              <button
                key={f.key}
                onClick={() => handleFilterChange(f.key)}
                className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                style={{
                  background: filter === f.key ? BRAND : "#F3F4F6",
                  color: filter === f.key ? "#fff" : "#6B7280",
                }}
              >
                {f.label}
                <span className="ml-1.5 opacity-70">{cnt}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 流程说明卡 */}
      <div className="mx-4 mt-3 mb-1 rounded-2xl overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1A2238 0%,#2D3561 50%,#3D5A80 100%)" }}
      >
        <div className="px-4 pt-3 pb-0">
          <div className="flex items-center gap-2">
            <i className="ri-route-line" style={{ color: "#FF6B35", fontSize: 13 }} />
            <span className="text-xs font-bold text-white">需求询单流程</span>
          </div>
        </div>
        <div className="px-3 pb-4 pt-3">
          <div className="grid grid-cols-4 gap-1">
            {[
              { step: "提交询单", desc: "AI选定后提交", icon: "ri-send-plane-fill" },
              { step: "顾问报价", desc: "顾问设置价格", icon: "ri-user-star-fill" },
              { step: "确认支付", desc: "确认方案付款", icon: "ri-bank-card-fill" },
              { step: "成单出行", desc: "转为正式订单", icon: "ri-checkbox-circle-fill" },
            ].map((s, i) => (
              <div key={s.step} className="flex flex-col items-center">
                {/* 圆形图标 */}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(249,115,22,0.22)",
                    border: "1.5px solid rgba(249,115,22,0.45)",
                  }}
                >
                  <i className={s.icon} style={{ color: "#F97316", fontSize: 14 }} />
                </div>
                {/* 文字 */}
                <div className="text-center mt-1.5">
                  <div className="text-[11px] font-semibold leading-none text-orange-400">{s.step}</div>
                  <div className="text-[9px] text-white/30 leading-tight mt-0.5">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {/* 底部连接线 */}
          <div className="flex items-center px-6 mt-[-18px] mb-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-px flex-1 mx-0.5"
                style={{ background: `linear-gradient(90deg, rgba(255,255,255,${0.25 - i * 0.04}), rgba(255,255,255,0.06))` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 询单列表 */}
      <div className="px-4 py-2 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <i
              className="ri-loader-4-line animate-spin"
              style={{ fontSize: 32, color: BRAND }}
            />
            <span className="text-xs text-gray-400 mt-3">加载中...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "#FFF3EE" }}>
              <i className="ri-file-search-line" style={{ color: BRAND, fontSize: 28 }} />
            </div>
            <div className="text-sm font-medium text-gray-500 mb-1">暂无询单</div>
            <div className="text-xs text-gray-400">去AI规划选定产品后提交询单</div>
            <button
              onClick={() => navigate("/ai-plan")}
              className="mt-4 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap text-white"
              style={{ background: BRAND }}
            >
              去AI规划
            </button>
          </div>
        ) : (
          filtered.map((order) => {
            const rawItem = rawList.find((r) => r.id === Number(order.id));
            return (
              <InquiryCard
                key={order.id}
                order={order}
                onClick={() => handleCardClick(order, rawItem?.id)}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
